import { z } from 'zod';
import {
  filterContainerSchema,
  frozenSelectionSnapshotSchema,
} from '../selection/snapshot/selectionFilterSerialization.schemas';

export const MUTATION_LEDGER_VERSION = 1;

export const jsonValueSchema = z.json();

/** Runtime schema for the complete metadata stored with every derived-column action. */
export const derivedColumnMetadataSchema = z.object({
  name: z.string(),
  description: z.string().nullish().default(null),
  valueType: z.string(),
  groupId: z.string().nullable(),
  groupName: z.string().nullable(),
  groupDescription: z.string().nullish().default(null),
  decimalDigits: z.number().nullable(),
});

/** Runtime schema for final derived-column-group summaries. */
export const derivedColumnGroupMetadataSchema = z.object({
  name: z.string(),
  description: z.string().nullish().default(null),
});

const joinColumnSchema = z.object({
  sourceColId: z.string(),
  destColId: z.string(),
  columnMetadata: derivedColumnMetadataSchema,
});

const tableJoinSchema = z.object({
  type: z.literal('TABLE_JOIN'),
  sourceTableId: z.string(),
  destTableId: z.string(),
  sourceMatchingColId: z.string(),
  destMatchingColId: z.string(),
  aggregationType: z.string().optional(),
  columns: z.array(joinColumnSchema),
  sourceFilter: filterContainerSchema.optional(),
});

const asyncColumnSchema = z.object({
  type: z.literal('ASYNC_COLUMN'),
  sourceDataTableId: z.string(),
  columnId: z.string(),
  columnMetadata: derivedColumnMetadataSchema,
});

const propertyGroupColumnSchema = z.object({
  type: z.literal('PROPERTY_GROUP_COLUMN'),
  propertyGroup: z.string(),
  sourceTableId: z.string(),
  rowId: z.string(),
  columnMetadata: derivedColumnMetadataSchema,
});

const expressionColumnSchema = z.object({
  type: z.literal('EXPRESSION'),
  expression: z.string(),
  paramNameSelectedState: z.string(),
  usedColIds: z.array(z.string()),
  variables: z.record(z.string(), jsonValueSchema),
  frozenSelectionSnapshot: frozenSelectionSnapshotSchema.optional(),
  columnMetadata: derivedColumnMetadataSchema,
});

export const derivedColumnDefinitionSchema = z.discriminatedUnion('type', [
  tableJoinSchema,
  asyncColumnSchema,
  propertyGroupColumnSchema,
  expressionColumnSchema,
]);

/** Runtime schema for one ordered mutation-ledger action. */
export const ledgerActionSchema = z.union([
  z.object({
    type: z.union([z.literal('ADD_COLUMN'), z.literal('UPDATE_COLUMN')]),
    targetTableId: z.string(),
    targetColumnIds: z.array(z.string()),
    payload: derivedColumnDefinitionSchema,
  }),
  z.object({
    type: z.literal('DELETE_COLUMN'),
    targetTableId: z.string(),
    targetColumnIds: z.array(z.string()),
  }),
]);

/** Runtime schema for the ordered mutation history held by a data context. */
export const MutationLedgerSchema = z.object({
  version: z.literal(MUTATION_LEDGER_VERSION),
  actions: z.array(ledgerActionSchema),
});

/** Runtime schema for the version-one serialized mutation ledger payload. */
export const MutationLedgerPayloadV1Schema = z.object({
  version: z.literal(MUTATION_LEDGER_VERSION),
  sourceAssetId: z.string(),
  sourceAssetName: z.string(),
  assetType: z.string(),
  columnInfo: z.record(z.string(), z.record(z.string(), derivedColumnMetadataSchema)),
  columnGroupInfo: z.record(z.string(), z.record(z.string(), derivedColumnGroupMetadataSchema)),
  actions: z.array(ledgerActionSchema),
});

export type JsonValue = z.infer<typeof jsonValueSchema>;
export type DerivedColumnMetadata = z.infer<typeof derivedColumnMetadataSchema>;
export type DerivedColumnGroupMetadata = z.infer<typeof derivedColumnGroupMetadataSchema>;
export type AddColumnTableJoin = z.infer<typeof tableJoinSchema>;
export type AddColumnAsync = z.infer<typeof asyncColumnSchema>;
export type AddColumnFromPropertyGroups = z.infer<typeof propertyGroupColumnSchema>;
export type AddColumnFromExpression = z.infer<typeof expressionColumnSchema>;
export type DerivedColumnDefinition = z.infer<typeof derivedColumnDefinitionSchema>;
export type LedgerAction = z.infer<typeof ledgerActionSchema>;
export type AddColumnAction = {
  type: 'ADD_COLUMN';
  targetTableId: string;
  targetColumnIds: string[];
  payload: DerivedColumnDefinition;
};
export type MutationLedger = z.infer<typeof MutationLedgerSchema>;
export type MutationLedgerPayloadV1 = z.infer<typeof MutationLedgerPayloadV1Schema>;
export type TpMutationLedgerPayloadV1 = MutationLedgerPayloadV1;

/** Create a new derived column metadata object.
 *
 * @param input - The input data for the new derived column metadata.
 * @returns A new DerivedColumnMetadata object.
 */
export function createColumnMetadata(input: {
  name: string;
  description?: string | null;
  valueType: string;
  groupId?: string | null;
  groupName?: string | null;
  groupDescription?: string | null;
  decimalDigits?: number | null;
}): DerivedColumnMetadata {
  return {
    name: input.name,
    description: input.description ?? null,
    valueType: input.valueType,
    groupId: input.groupId ?? null,
    groupName: input.groupName ?? null,
    groupDescription: input.groupDescription ?? null,
    decimalDigits: input.decimalDigits ?? null,
  };
}

/**
 * Create a ledger action for adding a new column to a table.
 */
export function createAddColumnAction({
  targetTableId,
  targetColumnIds,
  payload,
}: {
  targetTableId: string;
  targetColumnIds: string[];
  payload: DerivedColumnDefinition;
}): AddColumnAction {
  return {
    type: 'ADD_COLUMN',
    targetTableId,
    targetColumnIds,
    payload,
  };
}
